import React from "react";
import { Link, useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import { configFor, SITE } from "../lib/seoConfig";
import { ArrowRight, Check, ChevronRight, Sparkles, BookOpen, MessageCircleQuestion, TrendingUp } from "lucide-react";

/**
 * Configuration for each keyword landing page — pulls H1/intent from seoConfig,
 * adds H2 sections, FAQ items, and internal links unique to each cluster.
 * Keeping this co-located so adding a new landing page is one block in one file.
 */
const PAGE_CONTENT = {
  "/essay-writing-help": {
    accent: "indigo",
    hero: {
      eyebrow: "Essay writing help",
      lead: "Coach students through every essay — argumentative, narrative, expository, analytical — with rubric-aligned AI feedback at the paragraph level.",
    },
    sections: [
      { h2: "From topic to thesis",
        body: "Turn a vague topic into a defensible thesis statement. WriterSpark walks students through audience, claim, and counterclaim before the first sentence of the draft.",
        bullets: ["Topic → claim → thesis scaffolding", "Counterclaim brainstorming", "Audience-aware tone coaching"] },
      { h2: "Body paragraphs that actually argue",
        body: "Use TEAR, PEEL, or CER frameworks to teach evidence integration and analysis. Coach prompts ask 'so what?' until students explain their reasoning.",
        bullets: ["Evidence integration coaching", "Analysis depth probes", "Transition variety scoring"] },
      { h2: "Rubric-aligned feedback",
        body: "WriterSpark scores essays on standards-aligned rubrics (ideas, organization, voice, conventions) and explains every score with specific examples from the student's own writing.",
        bullets: ["Rubric scores with student-facing explanations", "Highlights from the student's actual draft", "Targeted next-step suggestions"] },
    ],
    faqs: [
      { q: "Will WriterSpark write my essay for me?",
        a: "No. WriterSpark coaches — it never writes the student's draft. The platform asks questions, models thinking, and gives revision feedback, but the writing stays the student's own work." },
      { q: "What grade levels does WriterSpark support?",
        a: "Elementary, middle, and high school — feedback tone and complexity automatically adapts to the selected grade band." },
      { q: "Can I track essay growth over time?",
        a: "Yes. Revision Intelligence tracks every draft of an essay and shows rubric score changes across drafts." },
    ],
    relatedLinks: [
      "/argumentative-writing-support",
      "/narrative-writing-support",
      "/thesis-writing-help",
      "/writing-revision-strategies",
    ],
  },

  "/argumentative-writing-support": {
    accent: "rose",
    hero: {
      eyebrow: "Argumentative writing",
      lead: "Coach students through claim, counterclaim, evidence, and reasoning — the way teachers actually grade argumentative essays.",
    },
    sections: [
      { h2: "Build defensible claims",
        body: "Stop students from writing 'I think dogs are good.' Practice writing arguable claims with stakes — and coach them when the claim is too obvious or too vague.",
        bullets: ["Claim arguability check", "Stakes and significance probes", "Counterclaim balance"] },
      { h2: "Evidence + reasoning, not just facts",
        body: "Integrate sources, then explain the inferential leap. WriterSpark coaches the analytical move from evidence to claim.",
        bullets: ["Source integration coaching", "Warrant / reasoning probes", "Refutation practice"] },
      { h2: "Address counterclaims with grace",
        body: "Strong arguments concede something. Students practice acknowledging the strongest version of the opposing view before refuting.",
        bullets: ["Counterclaim brainstorming", "Concession + refutation templates", "Rhetorical move analysis"] },
    ],
    faqs: [
      { q: "Does WriterSpark teach Claim-Evidence-Reasoning (CER)?",
        a: "Yes — CER is one of the built-in frameworks, alongside Toulmin's claim/data/warrant and the standard argumentative essay structure." },
      { q: "Is this useful for SAT/AP argument essays?",
        a: "Yes. SAT/AP Prep mode runs timed argument essays scored on the College Board rubric with specific revision targets." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/constructed-response-strategies",
      "/thesis-writing-help",
      "/writing-revision-strategies",
    ],
  },

  "/constructed-response-strategies": {
    accent: "amber",
    hero: {
      eyebrow: "Constructed response",
      lead: "Master constructed responses with RACE and ACE strategies. Instant feedback on text-based evidence, analysis, and explanation.",
    },
    sections: [
      { h2: "RACE: Restate, Answer, Cite, Explain",
        body: "Walk students through the RACE strategy used on state assessments — with prompts that catch when one of the four moves is missing.",
        bullets: ["Restate detection", "Direct answer coaching", "Citation quality check", "Explanation depth probe"] },
      { h2: "ACE: Answer, Cite, Explain",
        body: "Streamlined response format for middle school. WriterSpark coaches students to use textual evidence — not vague references.",
        bullets: ["Evidence specificity coach", "Quote integration", "Explanation length checks"] },
      { h2: "Text-based writing across content areas",
        body: "Practice constructed responses across ELA, science (CER), and social studies (DBQ) — all in WriterSpark's cross-curricular literacy mode.",
        bullets: ["ELA short response", "Science CER", "Social studies DBQ"] },
    ],
    faqs: [
      { q: "Which constructed response strategy is best?",
        a: "RACE works well for upper middle / high school. ACE is cleaner for younger writers. WriterSpark supports both — pick what your school uses." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/argumentative-writing-support",
      "/writing-revision-strategies",
    ],
  },

  "/writing-intervention-tools": {
    accent: "emerald",
    hero: {
      eyebrow: "Writing intervention",
      lead: "Diagnose where students struggle — grammar, structure, evidence, clarity — and coach them with grade-adapted feedback that targets the exact gap.",
    },
    sections: [
      { h2: "Diagnose, then prescribe",
        body: "Rubric analysis surfaces the specific dimension that's holding a writer back — organization, conventions, voice, or ideas — and routes them to targeted drills.",
        bullets: ["Per-dimension rubric scoring", "Pattern detection across drafts", "Auto-routing to skill drills"] },
      { h2: "MTSS-friendly progress monitoring",
        body: "Tiered intervention support — track each student's growth across drafts with exportable rubric trend data for IEP and 504 meetings.",
        bullets: ["Draft-over-draft growth charts", "Per-rubric trend lines", "Mentor/teacher dashboards"] },
      { h2: "Built for tutors and schools",
        body: "Teachers and parents can link students via invite code or email — see exactly which writing skills are improving and which need re-teaching.",
        bullets: ["Classroom roster", "Family roster", "Per-student detail views"] },
    ],
    faqs: [
      { q: "Is WriterSpark MTSS-aligned?",
        a: "WriterSpark's rubric-based diagnostics and progress monitoring work well inside MTSS (Multi-Tiered Systems of Support) for writing intervention, though specific tier placement remains the educator's decision." },
    ],
    relatedLinks: [
      "/student-writing-improvement",
      "/ai-writing-feedback-for-students",
      "/writing-revision-strategies",
    ],
  },

  "/student-writing-improvement": {
    accent: "indigo",
    hero: {
      eyebrow: "Student writing improvement",
      lead: "Daily prompts, rubric scoring, revision intelligence, and growth analytics — from elementary through high school, in one place.",
    },
    sections: [
      { h2: "Daily writing practice",
        body: "Short, grade-adapted prompts that build fluency — fiction, opinion, expository, and timed sprints. Streaks keep students coming back.",
        bullets: ["Daily prompts", "Streaks + badges", "Growth Paths from Bronze to Diamond"] },
      { h2: "Rubric scoring and trend tracking",
        body: "Every graded piece feeds the student's growth chart. Parents and teachers see exactly where writing is improving.",
        bullets: ["Per-rubric trend lines", "Visible draft history", "Growth charts in mentor dashboards"] },
      { h2: "Coaching across writing types",
        body: "Argumentative, narrative, expository, analytical, and SAT/AP essays — all use the same coaching engine with rubric-aligned feedback.",
        bullets: ["Essay coach", "Constructed response", "SAT/AP timed essays", "Cross-curricular CER and DBQ"] },
    ],
    faqs: [
      { q: "How long until I see improvement?",
        a: "Students who complete 3-5 sessions per week typically see measurable rubric score improvements within 4-6 weeks, especially in conventions and organization." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/ai-writing-feedback-for-students",
      "/writing-intervention-tools",
    ],
  },

  "/ai-writing-feedback-for-students": {
    accent: "violet",
    hero: {
      eyebrow: "AI writing feedback",
      lead: "AI writing feedback designed for student growth — rubric scores, specific revision suggestions, grade-banded tone. Built to coach, never to write for them.",
    },
    sections: [
      { h2: "Coaching, not auto-writing",
        body: "WriterSpark never generates the student's draft. The AI's role is to ask questions, model thinking, and give pointed feedback — so the writing stays the student's own.",
        bullets: ["No auto-generated drafts", "Socratic question prompts", "Coach explains every score"] },
      { h2: "Rubric-aligned, grade-adapted",
        body: "Feedback uses age-appropriate language and rubric vocabulary that matches the student's grade band — without dumbing it down.",
        bullets: ["Elementary tone", "Middle school precision", "High school analytical vocabulary"] },
      { h2: "Specific, not generic",
        body: "Every comment points to a specific phrase in the student's writing. No 'good job' filler — every score comes with what to revise and why.",
        bullets: ["Phrase-level highlighting", "Revision targets per draft", "Actionable next-step suggestions"] },
    ],
    faqs: [
      { q: "Which AI model powers WriterSpark?",
        a: "WriterSpark uses Anthropic's Claude Sonnet 4.5 via the Emergent integration platform. The model is tuned with educator-written system prompts that enforce the 'coach, don't write' rule." },
      { q: "Is student writing used to train AI?",
        a: "No. Student writing is processed for inference only and not used to train any underlying AI model." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/argumentative-writing-support",
      "/student-writing-improvement",
    ],
  },

  "/narrative-writing-support": {
    accent: "rose",
    hero: {
      eyebrow: "Narrative writing",
      lead: "Coach scene, character, dialogue, and pacing — with feedback aligned to common narrative rubrics for personal and creative pieces.",
    },
    sections: [
      { h2: "Show, don't tell",
        body: "Identify weak narrative moves ('she was sad') and coach concrete sensory revision ('she folded the letter twice and stared at the kitchen tile').",
        bullets: ["Sensory detail prompts", "Show-don't-tell flags", "Active vs. passive voice"] },
      { h2: "Pacing and structure",
        body: "Coach Freytag arc, in medias res, and flashback structure — with feedback on whether the climax actually earns its weight.",
        bullets: ["Plot arc checks", "Climax weight analysis", "Pacing rhythm"] },
      { h2: "Voice and dialogue",
        body: "Develop a consistent narrative voice and dialogue that sounds like actual people — not exposition in quotes.",
        bullets: ["Voice consistency", "Dialogue beats", "Subtext detection"] },
    ],
    faqs: [
      { q: "Is this for personal narratives or creative writing?",
        a: "Both. Personal narrative mode is grade-adapted for elementary/middle school memoir-style pieces; creative writing mode supports short story drafts." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/paragraph-organization-practice",
      "/writing-revision-strategies",
    ],
  },

  "/thesis-writing-help": {
    accent: "amber",
    hero: {
      eyebrow: "Thesis writing",
      lead: "Build arguable, specific thesis statements — the difference between a topic, a claim, and a defensible thesis, with examples.",
    },
    sections: [
      { h2: "Topic ≠ claim ≠ thesis",
        body: "Many drafts collapse because the 'thesis' is actually just a topic. WriterSpark teaches the three-step move from topic to claim to defensible thesis.",
        bullets: ["Topic detection", "Claim arguability check", "Thesis specificity score"] },
      { h2: "Argumentative vs. analytical thesis",
        body: "Different essay types need different thesis types — argumentative theses take a side, analytical theses make a defensible interpretation.",
        bullets: ["Argumentative thesis builder", "Analytical thesis builder", "Comparative thesis builder"] },
      { h2: "Live examples and revision",
        body: "Students see weak → strong thesis revisions in their own grade level, then practice on their own draft with immediate feedback.",
        bullets: ["Before/after examples", "Grade-banded models", "Live thesis revision coach"] },
    ],
    faqs: [
      { q: "Where does the thesis belong in an essay?",
        a: "Conventionally at the end of the introduction. WriterSpark also coaches delayed-thesis structures used in narrative-driven argumentative essays." },
    ],
    relatedLinks: [
      "/argumentative-writing-support",
      "/essay-writing-help",
      "/paragraph-organization-practice",
    ],
  },

  "/paragraph-organization-practice": {
    accent: "emerald",
    hero: {
      eyebrow: "Paragraph organization",
      lead: "Practice writing tight body paragraphs that actually argue — TEAR, PEEL, and TEEL frameworks scaffolded so students stop writing one giant block.",
    },
    sections: [
      { h2: "TEAR: Topic, Evidence, Analysis, Reasoning",
        body: "Practice the TEAR structure for analytical body paragraphs — with prompts that catch missing analysis or unsupported claims.",
        bullets: ["Topic sentence focus", "Evidence specificity", "Analysis depth", "Reasoning warrant"] },
      { h2: "PEEL & TEEL",
        body: "Point, Evidence, Explain, Link — and its TEEL variant. WriterSpark supports both and shows which framework matches each prompt type.",
        bullets: ["PEEL paragraph builder", "TEEL paragraph builder", "Link sentence coaching"] },
      { h2: "From outline to paragraph",
        body: "Build a paragraph outline first, then expand each move into sentences with live feedback — instead of writing then discovering the structure is broken.",
        bullets: ["Outline mode", "Sentence-level expansion", "Cohesion check"] },
    ],
    faqs: [
      { q: "Which paragraph structure should my students use?",
        a: "TEAR works well for analytical / argumentative writing. PEEL is great for literary analysis. WriterSpark supports both." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/argumentative-writing-support",
      "/thesis-writing-help",
    ],
  },

  "/writing-revision-strategies": {
    accent: "violet",
    hero: {
      eyebrow: "Writing revision",
      lead: "Revise with intent — and watch the rubric scores climb. WriterSpark tracks every draft and shows students what changed and why.",
    },
    sections: [
      { h2: "Draft-over-draft tracking",
        body: "Every revision is a tracked event. Students and teachers see rubric score changes across drafts — proof that revision works.",
        bullets: ["Per-draft rubric scores", "Diff between drafts", "Revision Cycles dashboard"] },
      { h2: "Targeted revision moves",
        body: "Don't just say 'revise.' WriterSpark gives one specific revision goal per pass — strengthen evidence, tighten transitions, vary sentence length.",
        bullets: ["One revision goal per pass", "Mini-lesson on the move", "Practice paragraphs"] },
      { h2: "Coach 'see the draft like film'",
        body: "The Interactive Writing Conference walks students through a Socratic revision conversation — brainstorm → draft → revise → reflect.",
        bullets: ["Socratic conference coach", "Phase-aware prompts", "Reflection journaling"] },
    ],
    faqs: [
      { q: "How does WriterSpark track revisions?",
        a: "Each version of an essay is saved with its rubric scores; the Revision Intelligence dashboard shows growth across drafts." },
    ],
    relatedLinks: [
      "/essay-writing-help",
      "/student-writing-improvement",
      "/ai-writing-feedback-for-students",
    ],
  },
};

function Breadcrumbs({ path, label }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6">
      <Link to="/" className="hover:text-slate-700">Home</Link>
      <ChevronRight size={14} className="inline mx-1 -mt-0.5" />
      <span className="text-slate-800 font-semibold">{label}</span>
    </nav>
  );
}

export default function KeywordLanding() {
  const { pathname } = useLocation();
  const cfg = configFor(pathname);
  const content = PAGE_CONTENT[pathname];
  if (!content) {
    return (
      <div className="bg-white min-h-screen">
        <PublicHeader />
        <main className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Page not found</h1>
          <Link to="/" className="text-indigo-600 font-bold">Back to home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  // JSON-LD: BreadcrumbList + FAQPage (if any) + Article-ish
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.origin}/` },
        { "@type": "ListItem", position: 2, name: cfg.h1 || cfg.title, item: `${SITE.origin}${pathname}` },
      ],
    },
  ];
  if (content.faqs?.length) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: content.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <div className="bg-white min-h-screen">
      <Seo path={pathname} schema={jsonLd} />
      <PublicHeader />

      <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
        <Breadcrumbs path={pathname} label={cfg.h1 || cfg.title} />

        <header className="mb-12">
          <span className={`text-xs uppercase font-bold tracking-widest text-${content.accent}-700`}>
            {content.hero.eyebrow}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>
            {cfg.h1 || cfg.title}
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl">{content.hero.lead}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Link
              to="/register"
              data-testid="landing-cta-primary"
              className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-6 py-3 tactile-shadow"
            >
              Start free <ArrowRight size={16} />
            </Link>
            <Link
              to="/#pricing"
              data-testid="landing-cta-secondary"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-indigo-300 text-slate-900 font-bold rounded-full px-6 py-3"
            >
              See pricing
            </Link>
          </div>
        </header>

        <section className="space-y-10 mb-16">
          {content.sections.map((s, i) => (
            <article key={i}>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{s.h2}</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check size={16} className={`text-${content.accent}-600 mt-0.5 flex-shrink-0`} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        {content.faqs?.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Common questions</h2>
            <div className="space-y-4">
              {content.faqs.map((f, i) => (
                <details key={i} className="tactile-card p-5 group" data-testid={`landing-faq-${i}`}>
                  <summary className="cursor-pointer font-bold text-slate-900 flex items-center justify-between">
                    {f.q}
                    <ChevronRight size={16} className="transition-transform group-open:rotate-90" />
                  </summary>
                  <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {content.relatedLinks?.length > 0 && (
          <section className="mb-16">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "Outfit" }}>Keep exploring</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {content.relatedLinks.map((href) => {
                const c = configFor(href);
                return (
                  <Link
                    key={href}
                    to={href}
                    data-testid={`related-${href.replace(/^\//, "")}`}
                    className="tactile-card p-4 hover:border-indigo-300 transition flex items-start gap-3"
                  >
                    <Sparkles size={18} className="text-indigo-500 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-bold text-sm">{c.h1 || c.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.intent || c.description?.slice(0, 80)}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <section className="tactile-card p-8 sm:p-10 text-center mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>
            Ready to coach better writing?
          </h2>
          <p className="text-slate-600 mb-6 max-w-xl mx-auto">
            Start free — no credit card required. Upgrade when you're ready for unlimited coaching.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full px-7 py-3.5 tactile-shadow"
          >
            Start free <ArrowRight size={16} />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
